import { AxiosRequestConfig } from 'axios';
import { TFunction } from 'i18next';
import { toastr } from 'react-redux-toastr';
import { v4 as uuidv4 } from 'uuid';

import * as types from './actionTypes';
import API from '../constants/apiEndpoints';
import { appSyncItemNames } from '../constants/constants';
import { msalFetch } from '../components/auth/Auth-Utils';
import { AppSyncItemStatus } from '../models-enums';
import { IcomplianceCheckList, ThunkResult } from '../models';

/**
 * The one client-side definition of the name rule, mirroring the server's 500-character column.
 * Add and rename used to disagree - add sent the text raw - so a long entry showed one thing on the
 * tablet and another on the server until the job was downloaded again.
 */
const NAME_MAX_LENGTH = 500;
export const normalizeDeficiencyName = (name: string): string => (name || '').trim().slice(0, NAME_MAX_LENGTH);

/**
 * MMG-4335: pull the job's deficiency checklist during the job download so the rows are available
 * offline. They are seeded server-side when the asset joins the Repair job, so the FSE
 * only ever reads and mutates them. Mirrors getMaintenanceTriggersForFacility: resolves either way
 * so one failed snapshot cannot fail the whole download.
 */
export const getComplianceCheckListForJob = (jobID: string): ThunkResult<any> => {
    return dispatch => {
        const url = `${API.complianceCheckList.getForJob}?jobID=${jobID}`;
        // stamped so the reducer can tell this snapshot predates any edit made while it was in flight
        const requestedAt = Date.now();

        dispatch({
            type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
            name: appSyncItemNames.ComplianceCheckList,
            status: AppSyncItemStatus.inProgress
        });

        return msalFetch(url, { url, method: 'get' } as AxiosRequestConfig)
            .then((response: any) => {
                dispatch({
                    type: types.SET_COMPLIANCE_CHECKLIST,
                    payload: { data: response?.data || [], requestedAt }
                });
                dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.ComplianceCheckList });
            })
            .catch((error: any) => {
                console.error('[getComplianceCheckListForJob]:', error);
                dispatch({
                    type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                    name: appSyncItemNames.ComplianceCheckList,
                    status: AppSyncItemStatus.failed
                });
            });
    };
};

/**
 * Tick or untick a deficiency. The id is stable (server honours client ids on Add), so this is safe
 * to queue offline even for a row that has not synced yet.
 */
export const setComplianceCheckListVerified = (id: string, verified: boolean): ThunkResult<any> => {
    return (dispatch, getState) => {
        const previous = getState().complianceCheckList[id];
        const axiosOptions: AxiosRequestConfig = {
            url: API.complianceCheckList.setVerified,
            method: 'put',
            data: { id, verified }
        };

        dispatch({
            type: types.UPDATE_COMPLIANCE_CHECKLIST_VERIFIED,
            id,
            verified,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'Saving Deficiency Checklist' },
                    rollback: {
                        type: types.UPDATE_COMPLIANCE_CHECKLIST_VERIFIED_ROLLBACK,
                        id,
                        verified: previous ? previous.verified : false
                    }
                }
            }
        });
    };
};

/**
 * Add a deficiency the engineer typed in (MMG-4337, and the "add another" case of MMG-4335).
 * The id is generated here so the row can be ticked before it reaches the server.
 */
export const addComplianceCheckListItem = (jobID: string, installBaseID: string, name: string): ThunkResult<any> => {
    return dispatch => {
        const trimmed = normalizeDeficiencyName(name);
        const item: IcomplianceCheckList = {
            id: uuidv4(),
            name: trimmed,
            verified: false,
            jobID,
            installBaseID,
            measurementPointID: null,
            isDeleted: false,
            createDate: new Date().toISOString()
        };

        const axiosOptions: AxiosRequestConfig = {
            url: API.complianceCheckList.add,
            method: 'post',
            data: { id: item.id, jobID, installBaseID, name: trimmed }
        };

        dispatch({
            type: types.ADD_COMPLIANCE_CHECKLIST_ITEM,
            item,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'Adding Deficiency' },
                    commit: { type: types.ADD_COMPLIANCE_CHECKLIST_ITEM_COMMIT, id: item.id },
                    rollback: { type: types.ADD_COMPLIANCE_CHECKLIST_ITEM_ROLLBACK, item }
                }
            }
        });
    };
};

/**
 * Rename a deficiency the engineer typed in (MMG-4458). Carried-over rows are rejected by the server
 * whatever the app sends, so the check in the UI is convenience, not the control.
 */
export const updateComplianceCheckListName = (id: string, name: string): ThunkResult<any> => {
    return (dispatch, getState) => {
        const previous = getState().complianceCheckList[id];
        if (!previous) {
            return;
        }

        const trimmed = normalizeDeficiencyName(name);
        const axiosOptions: AxiosRequestConfig = {
            url: API.complianceCheckList.update,
            method: 'put',
            data: { id, name: trimmed }
        };

        dispatch({
            type: types.UPDATE_COMPLIANCE_CHECKLIST_NAME,
            id,
            name: trimmed,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'Saving Deficiency' },
                    commit: { type: types.UPDATE_COMPLIANCE_CHECKLIST_NAME_COMMIT, id },
                    rollback: { type: types.UPDATE_COMPLIANCE_CHECKLIST_NAME_ROLLBACK, id, name: previous.name }
                }
            }
        });
    };
};

/**
 * Remove a deficiency the engineer typed in (MMG-4458). Confirms first, the same way deleting a work
 * order does. The server soft-deletes, so the row survives as the record.
 */
export const deleteComplianceCheckListItem = (id: string, t: TFunction): ThunkResult<any> => {
    return (dispatch, getState) => {
        if (!getState().complianceCheckList[id]) {
            return;
        }

        const toastrConfirmOptions = {
            onOk: () => {
                const axiosOptions: AxiosRequestConfig = {
                    url: API.complianceCheckList.setDeleted,
                    method: 'put',
                    data: { id }
                };

                dispatch({
                    type: types.DELETE_COMPLIANCE_CHECKLIST_ITEM,
                    id,
                    meta: {
                        offline: {
                            effect: { axiosOptions, message: 'Removing Deficiency' },
                            commit: { type: types.DELETE_COMPLIANCE_CHECKLIST_ITEM_COMMIT, id },
                            rollback: { type: types.DELETE_COMPLIANCE_CHECKLIST_ITEM_ROLLBACK, id }
                        }
                    }
                });
            },
            onCancel: () => {
                return;
            },
            okText: t('common:delete'),
            cancelText: t('common:cancel')
        };

        toastr.confirm(t('toastMessage:deleteDeficiencyConfirm'), toastrConfirmOptions);
    };
};
